import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCalendarAlt } from "react-icons/fa";
import styles from "./styles.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomTextInput,
} from "../../../../components";
import AxiosInstance, {
  TokenService,
  sanitizeNameInput,
  hasInvalidNameChars,
} from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import { useSelector } from "react-redux";

const CreateFeeType = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);
  const editId = location.state?.ID || null;

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const user_id = user?.id || "";

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    // code: "",
    academicYear: "",
    description: "",
  });
  const [errors, setErrors] = useState({});

  // ── Academic Year dropdown options (from API) ──
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [academicYearLoading, setAcademicYearLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  useEffect(() => {
    if (editId) {
      fetchFeeTypeDetails();
    }
  }, [editId]);

  // ── Fetch Academic Year list on mount ──
  useEffect(() => {
    fetchAcademicYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id]);

  /** Academic Year list API: GET school/academic/{school_id} */
  const fetchAcademicYears = async () => {
    if (!school_id) return;
    setAcademicYearLoading(true);
    try {
      const response = await AxiosInstance.get(`/school/academic/${school_id}`);

      if (response?.data?.success && Array.isArray(response.data.data)) {
        const options = response.data.data
          .filter((item) => item.isActive === 1 || item.isUpcoming === 1)
          .map((item) => ({
            value: String(item.id),
            label: item.academic_year,
          }));
        setAcademicYearOptions(options);
      } else {
        setAcademicYearOptions([]);
      }
    } catch (error) {
      console.error("Error fetching academic years:", error);
      setAcademicYearOptions([]);
    } finally {
      setAcademicYearLoading(false);
    }
  };

  /** View API: schoolFees/view/fees_type  { id } */
  const fetchFeeTypeDetails = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("schoolFees/view/fees_type", {
        id: editId,
      });

      if (response?.data?.status) {
        const data = Array.isArray(response.data.data)
          ? response.data.data[0]
          : response.data.data;

        setFormData({
          name: data?.name || "",
          // code: data?.code || "",
          academicYear:
            data?.academic_year !== undefined && data?.academic_year !== null
              ? data.academic_year.toString()
              : "",
          description: data?.description || "",
        });
      }
    } catch (error) {
      console.error("Error fetching fee type details:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.somethingWrong ||
          "Failed to fetch fee type details. Please try again.",
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  /* Name rejects special characters and emojis as they are typed/pasted. */
  const handleNameChange = (e) => {
    const clean = sanitizeNameInput(e.target.value);
    setFormData((prev) => ({ ...prev, name: clean }));
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: "" }));
    }
  };

  const handleDropdownChange = (name, value) => {
    // Normalize: CustomDropdown may hand back a raw value, an option object,
    // or a synthetic event. Always store the underlying value.
    const finalValue =
      value && typeof value === "object"
        ? (value.value ?? value.target?.value ?? "")
        : value;

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = translations.labels?.nameRequired || "Name is required";
    } else if (hasInvalidNameChars(formData.name)) {
      // Safety net for values loaded from the API in edit mode
      newErrors.name =
        translations.labels?.specialCharactersNotAllowed ||
        "Special characters and emojis are not allowed";
    }
    // else if (!formData.code?.trim()) {
    //   newErrors.code = translations.labels?.codeRequired || "Code is required";
    // }
    if (!formData.academicYear) {
      newErrors.academicYear =
        translations.labels?.academicYearRequired ||
        "Academic Year is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setSubmitting(true);
    try {
      let response;

      if (editId) {
        /** Update API*/
        const payload = {
          id: editId,
          school_id: school_id,
          academic_id: Number(formData.academicYear),
          name: formData.name,
          // code: formData.code,
          description: formData.description,
          updated_by: user_id,
        };
        response = await AxiosInstance.post(
          "schoolFees/update/fees_type",
          payload,
        );
      } else {
        /** Create API*/
        const payload = {
          school_id: school_id,
          academic_id: Number(formData.academicYear),
          name: formData.name,
          // code: formData.code,
          description: formData.description,
          created_by: user_id,
        };
        response = await AxiosInstance.post(
          "schoolFees/create/fees_type",
          payload,
        );
      }

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: editId
            ? translations.labels?.feeTypeUpdatedSuccess ||
              "Fee type updated successfully!"
            : translations.labels?.feeTypeCreatedSuccess ||
              "Fee type created successfully!",
          redirectPath: "/user/listFeeType",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            translations.labels?.somethingWrong ||
            "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting fee type:", error);

      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          translations.labels?.somethingWrong ||
          "Something went wrong!",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Delete handler.
   * NOTE: no delete endpoint was provided for fee type, so this assumes the
   * same naming convention. Update the URL when you have the real one.
   */
  const handleDelete = async () => {
    try {
      const response = await AxiosInstance.put("schoolFees/delete/fees_type", {
        id: Number(editId),
        deleted_by: Number(user_id),
      });

      if (response?.data?.status) {
        setDeleteModalOpen(false);
        setModalState({
          open: true,
          type: "success",
          message: response?.data?.message || "Fee type deleted successfully!",
          redirectPath: "/user/listFeeType",
        });
      } else {
        setDeleteModalOpen(false);
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            "Failed to delete fee type. Please try again.",
          redirectPath: "",
        });
      }
    } catch (err) {
      console.error("Error deleting fee type:", err);
      setDeleteModalOpen(false);
      setModalState({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to delete fee type. Please try again.",
        redirectPath: "",
      });
    }
  };

  const handleCancel = () => {
    navigate("/user/listFeeType");
  };

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {editId
              ? translations.labels?.editFeeType || "Edit Fee Type"
              : translations.labels?.createFeeType || "Create Fee Type"}
          </h1>
        </div>
        <div className={styles.loadingContainer}>
          <p>{translations.labels?.loading || "Loading..."}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? translations.labels?.success || "Success"
            : translations.labels?.failed || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      <PopupModal
        open={deleteModalOpen}
        title={translations?.labels?.delete || "Confirm Delete"}
        message={
          translations?.labels?.confirmDeleteMessage ||
          "Are you sure you want to delete?"
        }
        onOk={handleDelete}
        onClose={() => setDeleteModalOpen(false)}
      />

      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {editId
              ? translations.labels?.editFeeType || "Edit Fee Type"
              : translations.labels?.createFeeType || "Create Fee Type"}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.name || "Name"}
                name="name"
                type="text"
                value={formData.name}
                onChange={handleNameChange}
                placeholder={
                  translations.labels?.enterFeeType || "Enter Fee Type"
                }
                required
                error={errors.name}
              />
            </div>

            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.academicYear || "Academic Year"}
                name="academicYear"
                value={formData.academicYear}
                onChange={(value) =>
                  handleDropdownChange("academicYear", value)
                }
                options={academicYearOptions}
                placeholder={
                  academicYearLoading
                    ? translations.labels?.loading || "Loading..."
                    : translations.labels?.selectAcademicYear ||
                      "Select academic year"
                }
                required
                error={errors.academicYear}
                disabled={!!editId || academicYearLoading}
              />
            </div>

            {/* <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={`${translations.labels?.code} `}
                name="code"
                type="text"
                required
                value={formData.code}
                onChange={handleInputChange}
                placeholder={translations.labels?.enterFeeCode || "e.g:TUI00I"}
                error={errors.code}
              />
            </div> */}

            <div className="col-lg-12 px-2 col-md-12 col-sm-12">
              <CustomTextInput
                label={`${translations.labels?.description} ${translations.labels?.optional}`}
                name="description"
                type="text"
                multiline
                rows={2}
                value={formData.description}
                onChange={handleInputChange}
                placeholder={translations.labels?.enterDescription || ""}
              />
            </div>
          </div>

          <div className={styles.buttonContainer}>
            {editId && (
              <CustomButton
                type="button"
                variant="contained"
                fullWidth={false}
                onClick={() => setDeleteModalOpen(true)}
                style={{
                  backgroundColor: "#dc3545",
                  borderColor: "#dc3545",
                  color: "#fff",
                }}
              >
                {translations?.labels?.delete || "Delete"}
              </CustomButton>
            )}

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations.labels?.cancel || "CANCEL"}
            </CustomButton>

            <CustomButton
              type="submit"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              loading={submitting}
              loadingText={
                editId
                  ? translations.labels?.updating || "Updating..."
                  : translations.labels?.submitting || "Submitting..."
              }
            >
              {editId
                ? translations.labels?.update || "UPDATE"
                : translations.labels?.submit || "SUBMIT"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateFeeType;
